// pages/CI/SLDReport.js
//
// CI-admin SLD report/viewer — port of the Mideast AdminSLDViewer + generateJobPDF
// (MEJobs.js), pointed at the MAIN commercial-sld module + main auth token.
// Pick Client → Site → completed Job, then explore the captured pins/photos/flags
// (GTA-style viewer) and export a iNOVAA PDF report.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Image as ImageIcon, Download, FileText, Flag, MapPin
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = BASE ? BASE.replace('/api', '') : '';
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const normBool = (v) => v === true || (typeof v === 'string' && v.toLowerCase() === 'yes');
const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + Number(p.x), 0) / pts.length,
  y: pts.reduce((s, p) => s + Number(p.y), 0) / pts.length,
});
const svgPts = (pts) => pts.map((p) => `${Number(p.x)},${Number(p.y)}`).join(' ');
const safeUrl = (baseUrl, url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};
const fmtDate = (v, opts) => (v ? new Date(v).toLocaleDateString('en-GB', opts || { day: 'numeric', month: 'long', year: 'numeric' }) : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : null);

// ─── THEME STYLES (AGGRESSIVE GLOBAL DARK MODE + ORIGINAL COLORS) ───────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@400;500&display=swap');

  /* Fallback / Light Mode Variables */
  :root, body, html, .csr-root {
    --bg-page: #F3F4F6;
    --bg-card: #ffffff;
    --bg-panel: #F9FAFB;
    --bg-panel-alt: #F3F4F6;
    --text-strong: #111827;
    --text-main: #374151;
    --text-muted: #6B7280;
    --border-main: #E5E7EB;
    --border-light: #F3F4F6;
    --accent-blue: #0EA5E9;
    --accent-blue-light: rgba(14, 165, 233, 0.1);
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.05);
    color-scheme: light;
  }

  /* Extreme Aggressive Dark Mode Selectors (Using exact previous colors) */
  html.dark, html.dark-mode, html[data-theme='dark'], html[data-bs-theme='dark'],
  body.dark, body.dark-mode, body[data-theme='dark'], body[data-bs-theme='dark'],
  .dark, .dark-mode, [data-theme='dark'], [data-bs-theme='dark'],
  .dark .csr-root, .dark-mode .csr-root, [data-theme='dark'] .csr-root, [data-bs-theme='dark'] .csr-root,
  .csr-root.dark, .csr-root[data-theme='dark'] {
    --bg-page: #050811 !important;
    --bg-card: #0D1120 !important;
    --bg-panel: #1E2535 !important;
    --bg-panel-alt: #161B2E !important;
    --text-strong: #F1F5F9 !important;
    --text-main: #E2E8F0 !important;
    --text-muted: rgba(255,255,255,0.4) !important;
    --border-main: rgba(255,255,255,0.1) !important;
    --border-light: rgba(255,255,255,0.05) !important;
    --accent-blue: #38BDF8 !important;
    --accent-blue-light: rgba(14, 165, 233, 0.15) !important;
    --shadow-sm: 0 4px 20px rgba(0,0,0,0.4) !important;
    color-scheme: dark !important;
  }

  .csr-root { font-family:'DM Sans',sans-serif; color:var(--text-main); background: var(--bg-page); min-height: 100vh; padding: 30px 20px; transition: background-color 0.3s ease, color 0.3s ease; }
  .csr-root * { box-sizing: border-box; transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease; }

  .md-page-title { font-size:24px; font-weight:800; color:var(--text-strong); }
  .md-page-sub { font-size:13px; color:var(--text-muted); margin-top:4px; font-family:'DM Mono',monospace; }
  
  .csr-card { background:var(--bg-card); border:1px solid var(--border-main); border-radius:16px; padding:24px; margin-bottom:20px; box-shadow: var(--shadow-sm); }
  
  .csr-pickers { display:flex; gap:16px; flex-wrap:wrap; }
  .csr-field { display:flex; flex-direction:column; gap:8px; min-width:240px; flex:1; }
  .csr-label { font-size:11px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; color:var(--text-muted); font-family:'DM Mono',monospace; }
  
  .csr-select { padding:12px 14px; background:var(--bg-panel); border:1px solid var(--border-main); border-radius:10px; color:var(--text-strong); font-size:14px; font-family:'DM Sans',sans-serif; outline:none; cursor:pointer; font-weight:600; }
  .csr-select:focus { border-color: var(--accent-blue); }
  .csr-select option { background: var(--bg-panel-alt); color: var(--text-strong); }

  .md-empty { text-align:center; color:var(--text-muted); font-size:14px; padding:48px 0; font-weight:500; }
  .md-empty-icon { width:56px; height:56px; border-radius:14px; background:var(--bg-panel); border:1px dashed var(--border-main); display:flex; align-items:center; justify-content:center; margin:0 auto 16px; color:var(--text-muted); }
  
  .md-spinner { display:inline-block; width:14px; height:14px; border:2px solid var(--text-muted); border-top-color:var(--accent-blue); border-radius:50%; animation:md-spin 0.7s linear infinite; }
  @keyframes md-spin { to { transform:rotate(360deg); } }

  .csr-layer-toggle { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; font-size:12px; font-weight:700; font-family:'DM Mono',monospace; cursor:pointer; border:1px solid var(--border-main); background:var(--bg-panel); color:var(--text-muted); }
  .csr-layer-toggle.on { color:var(--accent-blue); border-color:var(--accent-blue); background:var(--accent-blue-light); }
  
  .mj-pdf-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 16px; border-radius:10px; background:var(--accent-blue); border:1px solid var(--accent-blue); color:#fff; font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; cursor:pointer; transition:all 0.18s; }
  .mj-pdf-btn:hover:not(:disabled) { opacity: 0.9; }
  .mj-pdf-btn:disabled { opacity:0.45; cursor:not-allowed; }

  /* Map/Photo Viewer internal styles */
  .sld-image-container { position:relative; display:inline-block; max-width:100%; max-height:100%; }
  .gta-map-img { max-width:100%; max-height:520px; display:block; object-fit:contain; }
  .mj-svg-overlay { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible; }
  .mj-string-centroid-label { position:absolute; transform:translate(-50%,-50%); font-size:10px; font-weight:800; font-family:'DM Mono',monospace; border-radius:4px; padding:2px 7px; white-space:nowrap; background:rgba(0,0,0,0.75); color:#fff; backdrop-filter:blur(4px); pointer-events:none; z-index:10; }
  
  @keyframes flagPulse { 0%,100%{opacity:0.80;} 50%{opacity:1;} }
  .mj-string-flagged-polygon { animation:flagPulse 2s ease-in-out infinite; }
  
  .asv-back-btn { position:absolute; top:16px; left:16px; z-index:40; display:flex; align-items:center; gap:7px; padding:8px 16px; border-radius:10px; background:rgba(0,0,0,0.70); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.2); color:#fff; font-weight:700; font-size:13px; cursor:pointer; transition:all 0.2s; font-family:'DM Sans',sans-serif; }
  .asv-back-btn:hover { background:rgba(14,165,233,0.35); }
  .asv-bottom-bar { position:absolute; bottom:18px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:12px; z-index:40; background:rgba(0,0,0,0.8); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.15); padding:8px 20px; border-radius:14px; }

  /* Walking Animations */
  @keyframes asv-leg-fwd { 0% { transform: rotate(-30deg); } 50% { transform: rotate(30deg); } 100% { transform: rotate(-30deg); } }
  @keyframes asv-leg-bwd { 0% { transform: rotate(30deg); } 50% { transform: rotate(-30deg); } 100% { transform: rotate(30deg); } }
  @keyframes asv-arm-fwd { 0% { transform: rotate(25deg); } 50% { transform: rotate(-25deg); } 100% { transform: rotate(25deg); } }
  @keyframes asv-arm-bwd { 0% { transform: rotate(-25deg); } 50% { transform: rotate(25deg); } 100% { transform: rotate(-25deg); } }
  @keyframes asv-body-bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
  
  @keyframes asv-pinPulse { 0%,100%{box-shadow:0 0 0 0 rgba(34,211,165,0.5);} 60%{box-shadow:0 0 0 10px rgba(34,211,165,0);} }
  @keyframes asv-fadeUp { from{opacity:0;transform:translateX(-50%) translateY(6px);} to{opacity:1;transform:translateX(-50%) translateY(0);} }
  
  .mj-annot-item { padding:16px; border-radius:12px; background:var(--bg-panel); border:1px solid var(--border-main); margin-bottom:12px; }
  .mj-annot-photo { width:100%; border-radius:8px; object-fit:cover; max-height:240px; border:1px solid var(--border-main); margin-top:12px; }
  
  .mj-fsr-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-top:16px; }
  .mj-fsr-field { background:var(--bg-panel); border:1px solid var(--border-main); border-radius:10px; padding:12px 16px; }
  .mj-fsr-field-label { font-size:10px; font-weight:700; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.1em; color:var(--text-muted); margin-bottom:6px; }
  .mj-fsr-field-val { font-size:14px; font-weight:600; color:var(--text-strong); }
  
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-thumb { background: var(--border-main); border-radius: 4px; }
`;

// ─── PDF Generator (CI-adapted) ─────────────────────────────────────────────────
const generateCIJobPDF = async ({ detail, jobStrings, jobStringFlags, annotations, baseUrl }) => {
  const [jsPDFmod, html2canvas] = await Promise.all([import('jspdf'), import('html2canvas')]);
  const jsPDF = jsPDFmod.default || jsPDFmod.jsPDF;
  const h2c = html2canvas.default || html2canvas;

  const job = detail.job || {};
  const fsr = detail.fsr || null;

  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:fixed;left:-9999px;top:0;width:794px;background:#fff;font-family:DM Sans,Segoe UI,sans-serif;color:#111;';
  document.body.appendChild(wrap);

  const boolBadge = (val) =>
    `<span style="display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;font-family:DM Mono,monospace;background:${val ? '#dcfce7' : '#fee2e2'};color:${val ? '#16a34a' : '#dc2626'};">${val ? '✓ Yes' : '✗ No'}</span>`;
  const row = (label, value) => (value
    ? `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:9px 14px;font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;width:140px;vertical-align:top;">${label}</td><td style="padding:9px 14px;font-size:13px;color:#1e293b;font-weight:500;">${value}</td></tr>`
    : '');
  const section = (title, icon, content) =>
    `<div style="margin-bottom:24px"><div style="display:flex;align-items:center;gap:8px;padding:10px 16px;background:#f8fafc;border-left:4px solid #3b82f6;border-radius:0 8px 8px 0;margin-bottom:12px"><span style="font-size:15px">${icon}</span><span style="font-size:12px;font-weight:800;color:#1e293b;text-transform:uppercase;letter-spacing:0.1em">${title}</span></div>${content}</div>`;

  const tsRows = [
    { label: 'Reached Site', val: job.reached_location_at },
    { label: 'Reached Panels', val: job.started_at },
    { label: 'First Before Photo', val: job.before_photos_at },
    { label: 'Last After Photo', val: job.after_photos_at },
    { label: 'Completed', val: job.completed_at },
  ].filter((r) => r.val);

  const flaggedStrings = jobStrings.filter((s) => jobStringFlags.includes(s.id));

  const membersHtml = detail.members?.length
    ? `<table style="width:100%;border-collapse:collapse">${detail.members.map((m) => `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:9px 14px;font-size:13px;color:#1e293b;font-weight:600;">${m.firstname} ${m.lastname}</td><td style="padding:9px 14px;font-size:10px;font-weight:700;color:#d97706;font-family:DM Mono,monospace;text-transform:uppercase;">${m.role}</td><td style="padding:9px 14px;font-size:11px;color:#64748b;font-family:DM Mono,monospace;">${m.mobile || ''}</td></tr>`).join('')}</table>`
    : '';

  const fsrBoolFields = [
    { label: 'Panel Damage', key: 'panel_damage' },
    { label: 'Inverter Alarm', key: 'inverter_alarm' },
    { label: 'Potential Shading', key: 'potential_shading' },
    { label: 'Rusting', key: 'rusting' },
    { label: 'Bird Dropping', key: 'bird_dropping' },
    { label: 'Moss & Debris', key: 'mos_and_debris' },
    { label: 'Earthing', key: 'earthing' },
  ];
  const fsrGridHtml = fsr
    ? `<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:14px 0">${fsrBoolFields.map((f) => `<div style="background:#f8fafc;border-radius:8px;padding:10px 14px"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:5px">${f.label}</div>${boolBadge(normBool(fsr[f.key]))}</div>`).join('')}</div>`
    : '';

  const annotHtml = annotations.length
    ? `<table style="width:100%;border-collapse:collapse">${annotations.map((a) => `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px 14px;font-size:12px;color:#64748b;width:130px;font-weight:600">${a.point_label || '—'}${a.flag ? ' ⚠' : ''}</td><td style="padding:8px 14px;font-size:12px;color:#1e293b">${a.note || '—'}</td></tr>`).join('')}</table>`
    : '<p style="color:#94a3b8;font-size:12px;padding:0 14px">No field annotations</p>';

  wrap.innerHTML = `
    <div style="background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%);padding:28px 32px 22px;color:#fff;">
      <div style="display:flex;align-items:flex-start;justify-content:space-between">
        <div>
          <div style="font-size:22px;font-weight:900;letter-spacing:0.05em;color:#fff">iNOVAA</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.45);letter-spacing:0.12em;text-transform:uppercase;margin-top:2px">Field Service Report</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:11px;color:rgba(255,255,255,0.40);font-family:DM Mono,monospace;">Generated</div>
          <div style="font-size:12px;font-weight:700;color:rgba(255,255,255,0.75);font-family:DM Mono,monospace;">${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
      </div>
      <div style="margin-top:20px;padding-top:16px;border-top:1px solid rgba(255,255,255,0.12)">
        <div style="font-size:20px;font-weight:800;color:#fff">${job.site_name || '—'}</div>
        <div style="font-size:12px;color:rgba(255,255,255,0.50);margin-top:4px">${job.client_name || ''} · ${fmtDate(job.scheduled_date)}</div>
      </div>
    </div>
    <div style="padding:28px 32px">
      ${section('Job Details', '📋', `<table style="width:100%;border-collapse:collapse">${row('Site', job.site_name)}${row('Client', job.client_name)}${row('Location', job.location)}${row('Team', job.team_name || 'Unassigned')}${row('Date', fmtDate(job.scheduled_date))}${row('Panels Cleaned', fsr?.panels_cleaned)}</table>`)}
      ${membersHtml ? section('Team', '👷', membersHtml) : ''}
      ${tsRows.length ? section('Tracking Timestamps', '🕐', `<table style="width:100%;border-collapse:collapse">${tsRows.map((r) => row(r.label, fmtDateTime(r.val))).join('')}</table>`) : ''}
      ${flaggedStrings.length ? section('String Attention Flags', '⚠️', `<div style="display:flex;flex-wrap:wrap;gap:8px;padding:4px 14px">${flaggedStrings.map((s) => `<span style="padding:5px 14px;border-radius:20px;font-size:11px;font-weight:700;font-family:DM Mono,monospace;background:#fef2f2;color:#dc2626;border:1px solid #fecaca">⚠ ${s.label}</span>`).join('')}</div>`) : ''}
      ${fsr ? section('Field Service Report', '✅', `<table style="width:100%;border-collapse:collapse">${row('Cable Condition', fsr.cable_condition)}${row('Cable Quantity', fsr.cable_quantity)}${row('Panel Brand', fsr.panel_brand)}${row('Alarm Code', fsr.alarm_code)}${row('Shading Details', fsr.shading_details)}${row('Submitted At', fmtDateTime(fsr.submitted_at))}</table><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;padding:14px 14px 6px">Inspection Results</div>${fsrGridHtml}${fsr.observations ? `<div style="margin-top:10px;background:#f8fafc;border-radius:8px;padding:12px 14px"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">Observations</div><div style="font-size:13px;color:#1e293b;line-height:1.6">${fsr.observations}</div></div>` : ''}${fsr.signature_url ? `<div style="margin-top:14px;padding:14px;border:1px solid #e2e8f0;border-radius:8px;background:#fff"><div style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:10px">Client Signature</div><img src="${safeUrl(baseUrl, fsr.signature_url)}" crossorigin="anonymous" style="max-height:100px;max-width:260px;display:block;border:1px solid #e2e8f0;border-radius:6px;background:#fff"/></div>` : ''}`) : '<div style="padding:14px;color:#94a3b8;font-size:12px">FSR not submitted yet.</div>'}
      ${section('Field Annotations', '🔧', annotHtml)}
      <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;align-items:center">
        <span style="font-size:10px;color:#94a3b8;font-family:DM Mono,monospace">iNOVAA Platform · SoWash USA</span>
        <span style="font-size:10px;color:#94a3b8;font-family:DM Mono,monospace">Job #${job.id || '—'}</span>
      </div>
    </div>`;

  await new Promise((r) => setTimeout(r, 300));
  const canvas = await h2c(wrap, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', width: 794, windowWidth: 794 });
  document.body.removeChild(wrap);

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pdfW = pdf.internal.pageSize.getWidth();
  const pdfH = pdf.internal.pageSize.getHeight();
  const imgH = (canvas.height * pdfW) / canvas.width;
  let yOffset = 0;
  while (yOffset < imgH) {
    if (yOffset > 0) pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, -yOffset, pdfW, imgH, undefined, 'FAST');
    yOffset += pdfH;
  }
  const slug = (job.site_name || 'job').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const date = (job.scheduled_date || '').slice(0, 10);
  pdf.save(`NOVAA_Report_${slug}_${date}.pdf`);
};

// ─── GTA-style SLD Viewer ────────────────────────────────────────────────────────────
const AdminSLDViewer = ({
  diagram, photos, annotations, notes, strings, flaggedStrings,
  baseUrl, layerNotes, layerPhotos, layerAnnots, photosLoaded, isDark
}) => {
  const [, setView] = useState('map');
  const [activePoint, setActivePoint] = useState(null);
  const [photoType, setPhotoType] = useState('before');
  const [navDir, setNavDir] = useState('fwd');
  const [mapPhase, setMapPhase] = useState('visible');
  const [photoPhase, setPhotoPhase] = useState('hidden');
  const [zoomOrigin, setZoomOrigin] = useState('50% 50%');
  const [hoveredPin, setHoveredPin] = useState(null);
  const [isWalking, setIsWalking] = useState(false);
  const [flipHuman, setFlipHuman] = useState(false);

  useEffect(() => {
    setView('map'); setMapPhase('visible'); setPhotoPhase('hidden'); setActivePoint(null);
  }, [diagram]);

  const diagramUrl = diagram?.diagram_url ? safeUrl(baseUrl, diagram.diagram_url) : null;

  const pointList = useMemo(() => {
    if (!diagram?.points) return [];
    return diagram.points.map((pt, i) => {
      const pp = photos.filter((p) => p.point_id === pt.id);
      return {
        id: pt.id, index: i + 1, label: pt.label,
        x_percent: pt.x_percent, y_percent: pt.y_percent,
        hasPhoto: pp.length > 0,
        before: pp.find((p) => p.photo_type === 'before') || null,
        after: pp.find((p) => p.photo_type === 'after') || null,
      };
    });
  }, [diagram, photos]);

  const curIdx = pointList.findIndex((p) => p.id === activePoint?.id);
  const canGoBack = curIdx > 0;
  const canGoFwd = curIdx < pointList.length - 1;

  const getPinColor = (pointId) => {
    if (layerAnnots && annotations.some((a) => a.point_id === pointId)) return '#EF4444';
    if (layerNotes && notes?.some((n) => n.point_id === pointId)) return '#F59E0B';
    if (layerPhotos && photos.some((p) => p.point_id === pointId)) return '#22D3A5';
    return 'rgba(255,255,255,0.28)';
  };
  const getUrl = (p) => safeUrl(baseUrl, p?.photo_url);

  const handlePinClick = (pt) => {
    if (!pt.hasPhoto || mapPhase !== 'visible') return;
    
    if (activePoint) {
      setFlipHuman((pt.x_percent ?? 50) < (activePoint.x_percent ?? 50));
    }
    
    setZoomOrigin(`${pt.x_percent ?? 50}% ${pt.y_percent ?? 50}%`);
    setMapPhase('zooming');
    setTimeout(() => {
      setMapPhase('hidden'); setNavDir('fwd'); setActivePoint(pt);
      setPhotoType(pt.before ? 'before' : 'after'); setPhotoPhase('entering');
      setIsWalking(true); setTimeout(() => setIsWalking(false), 1100);
      setTimeout(() => { setView('photo'); setPhotoPhase('visible'); }, 30);
    }, 650);
  };

  const handleBack = () => {
    if (photoPhase !== 'visible') return;
    setPhotoPhase('leaving');
    setTimeout(() => {
      setPhotoPhase('hidden'); setView('map'); setMapPhase('zoomback');
      setTimeout(() => setMapPhase('visible'), 600);
    }, 380);
  };

  const navigate = (idx, dir) => {
    setNavDir(dir);
    const next = pointList[idx];
    
    if (activePoint) {
      setFlipHuman((next.x_percent ?? 50) < (activePoint.x_percent ?? 50));
    }

    setActivePoint(next); setPhotoType(next.before ? 'before' : 'after');
    setIsWalking(true); setTimeout(() => setIsWalking(false), 1100);
  };

  const mapStyle = () => {
    const base = { position: 'absolute', inset: 0, width: '100%', height: '100%', background: 'var(--bg-panel-alt)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' };
    if (mapPhase === 'visible') return { ...base, transform: 'scale(1)', opacity: 1, filter: 'blur(0)', transition: 'none' };
    if (mapPhase === 'zooming') return { ...base, transformOrigin: zoomOrigin, transform: 'scale(3)', opacity: 0, filter: 'blur(5px)', transition: 'transform 0.65s cubic-bezier(0.4,0,0.2,1), opacity 0.65s ease, filter 0.65s ease' };
    if (mapPhase === 'hidden') return { ...base, display: 'none' };
    if (mapPhase === 'zoomback') return { ...base, transformOrigin: zoomOrigin, transform: 'scale(1)', opacity: 1, filter: 'blur(0)', transition: 'transform 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease 0.1s' };
    return base;
  };
  
  const photoStyle = () => {
    const base = { position: 'absolute', inset: 0, background: '#000000' };
    if (photoPhase === 'hidden') return { ...base, opacity: 0, transform: 'scale(1.05)', transition: 'none', pointerEvents: 'none' };
    if (photoPhase === 'entering') return { ...base, opacity: 0, transform: 'scale(1.05)', transition: 'none', pointerEvents: 'none' };
    if (photoPhase === 'visible') return { ...base, opacity: 1, transform: 'scale(1)', transition: 'opacity 0.45s ease, transform 0.45s cubic-bezier(0.4,0,0.2,1)', pointerEvents: 'all' };
    if (photoPhase === 'leaving') return { ...base, opacity: 0, transform: 'scale(1.05)', transition: 'opacity 0.38s ease, transform 0.38s ease', pointerEvents: 'none' };
    return base;
  };

  const ArrowBtn = ({ side, onClick, label }) => {
    const [hov, setHov] = useState(false);
    return (
      <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ position: 'absolute', top: 0, bottom: 0, [side]: 0, width: '15%', zIndex: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: hov ? `linear-gradient(${side === 'left' ? '90' : '270'}deg,rgba(0,0,0,0.55) 0%,transparent 100%)` : `linear-gradient(${side === 'left' ? '90' : '270'}deg,rgba(0,0,0,0.20) 0%,transparent 100%)`, transition: 'background 0.25s ease' }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: hov ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.42)', border: `2px solid ${hov ? '#fff' : 'rgba(255,255,255,0.60)'}`, backdropFilter: 'blur(6px)', color: '#fff', transition: 'all 0.22s ease', transform: hov ? 'scale(1.13)' : 'scale(1)' }}>
          {side === 'left' ? <ChevronLeft size={22} strokeWidth={2.8} /> : <ChevronRight size={22} strokeWidth={2.8} />}
        </div>
        {hov && (
          <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: isDark ? 'rgba(0,0,0,0.82)' : 'rgba(255,255,255,0.95)', border: isDark ? '1px solid rgba(255,255,255,0.14)' : '1px solid #E5E7EB', borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 700, fontFamily: 'DM Mono,monospace', color: isDark ? '#fff' : '#111827', whiteSpace: 'nowrap', backdropFilter: 'blur(6px)', animation: 'asv-fadeUp 0.14s ease' }}>
            {side === 'left' ? `← ${label}` : `${label} →`}
          </div>
        )}
      </div>
    );
  };

  if (!diagram) return (<div className="md-empty"><div className="md-empty-icon"><ImageIcon size={24} /></div>No diagram for this site</div>);

  return (
    <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', background: 'var(--bg-card)', height: 520, border: '1px solid var(--border-main)', boxShadow: 'var(--shadow-md)' }}>
      <div style={mapStyle()}>
        {diagramUrl ? (
          <div className="sld-image-container">
            <img src={diagramUrl} alt="Site diagram" className="gta-map-img" draggable={false} />
            <svg className="mj-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
              {strings.map((s) => {
                const pts = Array.isArray(s.points) ? s.points : [];
                if (pts.length < 3) return null;
                const isFlagged = flaggedStrings.includes(s.id);
                return (
                  <polygon key={s.id} className={isFlagged ? 'mj-string-flagged-polygon' : ''} points={svgPts(pts)} fill={isFlagged ? `${s.color}35` : `${s.color}18`} stroke={s.color} strokeWidth={isFlagged ? '0.7' : '0.4'} strokeOpacity={isFlagged ? '0.95' : '0.60'} />
                );
              })}
            </svg>
            {strings.map((s) => {
              const pts = Array.isArray(s.points) ? s.points : [];
              if (pts.length < 3) return null;
              const isFlagged = flaggedStrings.includes(s.id);
              const c = centroid(pts);
              return (<span key={`lbl-${s.id}`} className="mj-string-centroid-label" style={{ left: `${c.x}%`, top: `${c.y}%`, color: s.color }}>{isFlagged ? '⚠ ' : ''}{s.label}</span>);
            })}
            {pointList.map((pt) => {
              const isH = hoveredPin === pt.id;
              const pinClr = getPinColor(pt.id);
              const clickable = pt.hasPhoto && photosLoaded;
              return (
                <div key={pt.id} onMouseEnter={() => setHoveredPin(pt.id)} onMouseLeave={() => setHoveredPin(null)} onClick={() => clickable && handlePinClick(pt)}
                  style={{ position: 'absolute', left: `${pt.x_percent ?? 50}%`, top: `${pt.y_percent ?? 50}%`, transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: clickable ? 'pointer' : 'default', zIndex: isH ? 30 : 10 }}>
                  {isH && (
                    <div style={{ position: 'absolute', bottom: '108%', left: '50%', transform: 'translateX(-50%)', background: isDark ? 'rgba(8,12,24,0.92)' : 'rgba(255,255,255,0.95)', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #E5E7EB', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, fontFamily: 'DM Mono,monospace', color: isDark ? '#E2E8F0' : '#111827', whiteSpace: 'nowrap', backdropFilter: 'blur(8px)', animation: 'asv-fadeUp 0.14s ease', pointerEvents: 'none' }}>
                      {pt.label || `Point ${pt.index}`}{pt.hasPhoto && <span style={{ color: '#38BDF8', marginLeft: 6 }}>▶ View</span>}
                    </div>
                  )}
                  <div style={{ width: isH ? 42 : 32, height: isH ? 42 : 32, borderRadius: '50%', background: pinClr !== 'rgba(255,255,255,0.28)' ? (isH ? `${pinClr}60` : `${pinClr}28`) : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.15)'), display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', animation: pt.hasPhoto ? 'asv-pinPulse 2.5s ease-in-out infinite' : 'none' }}>
                    <div style={{ width: isH ? 28 : 22, height: isH ? 28 : 22, borderRadius: '50%', background: pinClr !== 'rgba(255,255,255,0.28)' ? pinClr : (isDark ? 'rgba(255,255,255,0.28)' : '#9CA3AF'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, fontFamily: 'DM Mono,monospace', color: pinClr === 'rgba(255,255,255,0.28)' ? (isDark ? 'rgba(0,0,0,0.55)' : '#ffffff') : '#080C18', boxShadow: isH ? `0 0 0 3px rgba(255,255,255,0.3),0 0 18px ${pinClr}99` : '0 0 0 2px rgba(0,0,0,0.2),0 3px 8px rgba(0,0,0,0.2)', transition: 'all 0.2s ease' }}>{pt.index}</div>
                  </div>
                  {pt.label && <div style={{ background: isDark ? 'rgba(8,12,24,0.88)' : 'rgba(255,255,255,0.9)', borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 700, fontFamily: 'DM Mono,monospace', color: isDark ? '#E2E8F0' : '#111827', whiteSpace: 'nowrap', border: isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid #E5E7EB', backdropFilter: 'blur(4px)', pointerEvents: 'none', opacity: isH ? 1 : 0.78, transition: 'opacity 0.2s' }}>{pt.label}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>🗺️</span><span style={{ fontWeight: 600 }}>No diagram image</span>
          </div>
        )}
        <div style={{ position: 'absolute', top: 14, left: 14, color: 'var(--accent-blue)', fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', background: 'var(--bg-card)', padding: '5px 13px', borderRadius: 8, border: '1px solid var(--border-main)', zIndex: 10 }}>SLD Map</div>
        {!photosLoaded && (<div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', color: 'var(--text-strong)', fontSize: 11, fontWeight: 600, background: 'var(--bg-card)', border: '1px solid var(--border-main)', padding: '5px 14px', borderRadius: 20, zIndex: 10, whiteSpace: 'nowrap' }}>Loading photos…</div>)}
        {photosLoaded && (<div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, background: 'var(--bg-card)', border: '1px solid var(--border-main)', padding: '5px 14px', borderRadius: 20, zIndex: 10, whiteSpace: 'nowrap', pointerEvents: 'none' }}>Click a pin to view photos</div>)}
      </div>

      <div style={photoStyle()}>
        {activePoint && (
          <>
            <AnimatePresence initial={false} custom={navDir}>
              <motion.img
                key={activePoint.id + photoType}
                src={getUrl(activePoint[photoType])}
                alt={activePoint.label}
                draggable={false}
                custom={navDir}
                variants={{
                  enter: (dir) => ({ scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 0.6 : 1.4), opacity: 0, filter: 'blur(30px)', zIndex: dir === 'fwd' ? 1 : 3 }),
                  center: { scale: 1, opacity: 1, filter: 'blur(0px)', zIndex: 2 },
                  exit: (dir) => ({ scale: dir === 'fade' ? 1 : (dir === 'fwd' ? 1.4 : 0.6), opacity: 0, filter: 'blur(30px)', zIndex: dir === 'fwd' ? 3 : 1 }),
                }}
                initial="enter" animate="center" exit="exit"
                transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </AnimatePresence>
            <div style={{ zIndex: 40, position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <button onClick={handleBack} className="asv-back-btn" style={{ pointerEvents: 'all' }}>
                <ChevronLeft size={14} strokeWidth={2.5} /><span>Back to Map</span>
              </button>
              <div style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, pointerEvents: 'all' }}>
                {activePoint.before && (<button onClick={() => { setNavDir('fade'); setPhotoType('before'); }} style={{ padding: '6px 20px', borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: 'pointer', background: photoType === 'before' ? '#F59E0B' : 'rgba(0,0,0,0.65)', color: photoType === 'before' ? '#000' : 'rgba(255,255,255,0.75)', border: `1px solid ${photoType === 'before' ? '#F59E0B' : 'rgba(255,255,255,0.2)'}`, backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}>Before</button>)}
                {activePoint.after && (<button onClick={() => { setNavDir('fade'); setPhotoType('after'); }} style={{ padding: '6px 20px', borderRadius: 20, fontSize: 12, fontWeight: 800, cursor: 'pointer', background: photoType === 'after' ? '#22C55E' : 'rgba(0,0,0,0.65)', color: photoType === 'after' ? '#000' : 'rgba(255,255,255,0.75)', border: `1px solid ${photoType === 'after' ? '#22C55E' : 'rgba(255,255,255,0.2)'}`, backdropFilter: 'blur(8px)', transition: 'all 0.2s' }}>After</button>)}
              </div>
              {canGoBack && <div style={{ pointerEvents: 'all' }}><ArrowBtn side="left" onClick={(e) => { e.stopPropagation(); navigate(curIdx - 1, 'bwd'); }} label={pointList[curIdx - 1]?.label || `Point ${curIdx}`} /></div>}
              {canGoFwd && <div style={{ pointerEvents: 'all' }}><ArrowBtn side="right" onClick={(e) => { e.stopPropagation(); navigate(curIdx + 1, 'fwd'); }} label={pointList[curIdx + 1]?.label || `Point ${curIdx + 2}`} /></div>}
              <div className="asv-bottom-bar" style={{ pointerEvents: 'all' }}>
                <span style={{ fontWeight: 800, color: '#fff', fontSize: 13 }}>{activePoint.label || `Point ${activePoint.index}`}</span>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase', background: photoType === 'before' ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.25)', color: photoType === 'before' ? '#FCD34D' : '#86EFAC' }}>{photoType}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: 'DM Mono,monospace' }}>{curIdx + 1} / {pointList.length}</span>
              </div>
            </div>
            
            <div style={{ position: 'absolute', bottom: 58, right: 14, width: 220, height: 176, borderRadius: 10, background: isDark ? 'rgba(8,12,24,0.90)' : 'rgba(255,255,255,0.90)', border: '1px solid var(--accent-blue-light)', overflow: 'hidden', zIndex: 45, boxShadow: 'var(--shadow-md)', backdropFilter: 'blur(8px)' }}>
              <div style={{ position: 'absolute', top: 6, left: 8, fontSize: 9, fontWeight: 800, color: 'var(--accent-blue)', letterSpacing: 1, textTransform: 'uppercase', zIndex: 5, fontFamily: 'DM Mono,monospace' }}>📍 Location</div>
              <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 8px 8px' }}>
                
                <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: '100%' }}>
                  
                  <img src={diagramUrl} alt="mini" style={{ maxWidth: '100%', maxHeight: '144px', opacity: isDark ? 0.4 : 0.6, display: 'block', objectFit: 'contain' }} />
                  
                  {/* All points static background dots */}
                  {pointList.map((pt) => (
                    <div key={pt.id} style={{ 
                      position: 'absolute', left: `${pt.x_percent ?? 50}%`, top: `${pt.y_percent ?? 50}%`, transform: 'translate(-50%,-50%)', 
                      width: 5, height: 5, borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)', zIndex: 1 
                    }} />
                  ))}

                  {/* True Walking Animated SVG */}
                  {activePoint && (
                    <div style={{
                      position: 'absolute',
                      left: `${activePoint.x_percent ?? 50}%`,
                      top: `${activePoint.y_percent ?? 50}%`,
                      transform: `translate(-50%, -90%) scaleX(${flipHuman ? -1 : 1})`,
                      zIndex: 10,
                      transition: 'left 1.1s cubic-bezier(0.4, 0, 0.2, 1), top 1.1s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      <div style={{ position: 'absolute', bottom: -3, left: '50%', transform: 'translateX(-50%)', width: 12, height: 4, background: 'rgba(0,0,0,0.4)', borderRadius: '50%', filter: 'blur(2px)' }} />
                      <svg width="22" height="26" viewBox="0 0 24 24" style={{ 
                        overflow: 'visible', 
                        filter: isDark ? 'drop-shadow(0 0 6px rgba(56,189,248,0.8))' : 'drop-shadow(0 0 6px rgba(14,165,233,0.5))',
                        animation: isWalking ? 'asv-body-bounce 0.6s infinite ease-in-out' : 'none'
                      }}>
                        <g fill="none" stroke={isDark ? "#fff" : "#0EA5E9"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="4" r="2.5" fill={isDark ? "#fff" : "#0EA5E9"} />
                          <line x1="12" y1="6.5" x2="12" y2="12" />
                          <line x1="12" y1="7" x2="12" y2="12" style={{ transformOrigin: '12px 7px', animation: isWalking ? 'asv-arm-bwd 0.6s infinite ease-in-out' : 'none', transform: isWalking ? 'none' : 'rotate(15deg)' }} />
                          <line x1="12" y1="12" x2="12" y2="19" style={{ transformOrigin: '12px 12px', animation: isWalking ? 'asv-leg-bwd 0.6s infinite ease-in-out' : 'none', transform: isWalking ? 'none' : 'rotate(20deg)' }} />
                          <line x1="12" y1="12" x2="12" y2="19" style={{ transformOrigin: '12px 12px', animation: isWalking ? 'asv-leg-fwd 0.6s infinite ease-in-out' : 'none', transform: isWalking ? 'none' : 'rotate(-20deg)' }} />
                          <line x1="12" y1="7" x2="12" y2="12" style={{ transformOrigin: '12px 7px', animation: isWalking ? 'asv-arm-fwd 0.6s infinite ease-in-out' : 'none', transform: isWalking ? 'none' : 'rotate(-15deg)' }} />
                        </g>
                      </svg>
                    </div>
                  )}
                </div>

              </div>
            </div>
            {/* END MINIMAP */}

          </>
        )}
      </div>
    </div>
  );
};

const CISLDReport = () => {
  // Global Theme Observer
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark') ||
             document.body.classList.contains('dark') ||
             document.documentElement.getAttribute('data-theme') === 'dark' ||
             document.body.getAttribute('data-theme') === 'dark';
    }
    return false;
  });

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode')
      );
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState('');

  const [detail, setDetail] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const [strings, setStrings] = useState([]);
  const [flaggedStringIds, setFlaggedStringIds] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [layerNotes, setLayerNotes] = useState(true);
  const [layerPhotos, setLayerPhotos] = useState(true);
  const [layerAnnots, setLayerAnnots] = useState(true);

  // Clients
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE}/commercial-clients`);
        if (r.data?.success) setClients(r.data.data.clients || []);
      } catch (e) { console.error('[CI SLD Report] clients error:', e); }
    })();
  }, []);

  // Completed jobs for the client
  useEffect(() => {
    if (!selectedClientId) { setJobs([]); setSelectedSiteId(''); setSelectedScheduleId(''); return; }
    (async () => {
      try {
        const r = await axios.get(`${BASE}/fo-schedules/history?client_id=${selectedClientId}&status=completed`, authHdr());
        setJobs(r.data?.jobs || []);
      } catch (e) { console.error('[CI SLD Report] jobs error:', e); setJobs([]); }
    })();
    setSelectedSiteId(''); setSelectedScheduleId('');
  }, [selectedClientId]);

  const sites = useMemo(() => {
    const seen = new Map();
    jobs.forEach((j) => { if (j.site_id != null && !seen.has(String(j.site_id))) seen.set(String(j.site_id), j.site_name); });
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [jobs]);

  const siteJobs = useMemo(
    () => jobs.filter((j) => String(j.site_id) === String(selectedSiteId)),
    [jobs, selectedSiteId],
  );

  // Load everything for the chosen job
  const loadJob = useCallback(async (scheduleId, siteId) => {
    setLoading(true);
    setDetail(null); setDiagram(null); setStrings([]); setFlaggedStringIds([]);
    setPhotos([]); setAnnotations([]); setPhotosLoaded(false);
    try {
      const [detailRes, listRes] = await Promise.all([
        axios.get(`${BASE}/fo-schedules/${scheduleId}/detail`, authHdr()).catch(() => null),
        axios.get(`${BASE}/commercial-sld/diagrams/site/${siteId}`, authHdr()).catch(() => null),
      ]);
      if (detailRes?.data?.job) setDetail(buildDetail(detailRes.data.job));

      const list = listRes?.data?.diagrams || [];
      if (list.length) {
        const id = list[0].id;
        const [dRes, sRes, fRes, pRes, aRes] = await Promise.all([
          axios.get(`${BASE}/commercial-sld/diagrams/${id}`, authHdr()),
          axios.get(`${BASE}/commercial-sld/strings/diagram/${id}`, authHdr()).catch(() => ({ data: { strings: [] } })),
          axios.get(`${BASE}/commercial-sld/strings/job/${scheduleId}/flags`, authHdr()).catch(() => ({ data: { flags: [] } })),
          axios.get(`${BASE}/commercial-sld/point-photos/job/${scheduleId}`, authHdr()).catch(() => ({ data: { photos: [] } })),
          axios.get(`${BASE}/commercial-sld/annotations/job/${scheduleId}`, authHdr()).catch(() => ({ data: { annotations: [] } })),
        ]);
        setDiagram(dRes.data.diagram);
        setStrings(sRes.data.strings || []);
        setFlaggedStringIds((fRes.data.flags || []).map((f) => f.string_id));
        setPhotos(pRes.data.photos || []);
        setAnnotations(aRes.data.annotations || []);
        setPhotosLoaded(true);
      }
    } catch (e) {
      console.error('[CI SLD Report] load job error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedScheduleId && selectedSiteId) loadJob(selectedScheduleId, selectedSiteId);
  }, [selectedScheduleId, selectedSiteId, loadJob]);

  // Map the flat CI detail.job into the shape the PDF/sections expect
  const buildDetail = (job) => ({
    job: {
      id: job.schedule_id,
      site_name: job.site_name,
      client_name: job.client_name,
      location: [job.address, job.city].filter(Boolean).join(', '),
      team_name: job.team_lead_name,
      scheduled_date: job.scheduled_date,
      status: job.status,
      reached_location_at: job.reached_location_at,
      started_at: job.started_at,
      before_photos_at: job.before_photos_at,
      after_photos_at: job.after_photos_at,
      completed_at: job.completed_at,
    },
    fsr: (job.cable_condition || job.total_panels_cleaned) ? {
      panels_cleaned: job.total_panels_cleaned,
      cable_condition: job.cable_condition,
      cable_quantity: job.cable_quantity,
      panel_brand: job.panel_brand,
      alarm_code: job.alarm_code,
      shading_details: job.shading_details,
      panel_damage: job.panel_damage,
      inverter_alarm: job.inverter_alarm,
      potential_shading: job.potential_shading,
      rusting: job.rusting,
      bird_dropping: job.bird_dropping,
      mos_and_debris: job.mos_and_debris,
      earthing: job.earthing,
      observations: job.additional_notes,
      submitted_at: job.completed_at,
      signature_url: job.customer_signature ? `/uploads/signatures/${job.customer_signature}` : null,
    } : null,
    members: job.team_lead_name ? [{ firstname: job.team_lead_name, lastname: '', role: 'lead', mobile: job.team_phone || '' }] : [],
  });

  // Annotation layers for pin colour: flagged → red, noted → amber
  const flaggedAnnots = useMemo(() => annotations.filter((a) => normBool(a.flag) || a.flag === true), [annotations]);
  const notedAnnots = useMemo(() => annotations.filter((a) => a.note && !(normBool(a.flag) || a.flag === true)), [annotations]);

  const onExportPDF = async () => {
    if (!detail) return;
    setPdfBusy(true);
    try {
      await generateCIJobPDF({ detail, jobStrings: strings, jobStringFlags: flaggedStringIds, annotations, baseUrl: IMG_BASE });
    } catch (e) {
      console.error('[CI SLD Report] PDF error:', e);
      alert('Failed to generate PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const fsr = detail?.fsr;
  const boolFields = [
    ['Panel Damage', 'panel_damage'], ['Inverter Alarm', 'inverter_alarm'],
    ['Potential Shading', 'potential_shading'], ['Rusting', 'rusting'],
    ['Bird Dropping', 'bird_dropping'], ['Moss & Debris', 'mos_and_debris'], ['Earthing', 'earthing'],
  ];

  return (
    <div className="csr-root">
      <style>{STYLES}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <div className="md-page-title">SLD Reports</div>
          <div className="md-page-sub">View captured pins, photos &amp; flags for completed jobs · Export PDF</div>
        </div>

        <div className="csr-card">
          <div className="csr-pickers">
            <div className="csr-field">
              <span className="csr-label">Client</span>
              <select className="csr-select" value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div className="csr-field">
              <span className="csr-label">Site</span>
              <select className="csr-select" value={selectedSiteId} onChange={(e) => { setSelectedSiteId(e.target.value); setSelectedScheduleId(''); }} disabled={!selectedClientId || !sites.length}>
                <option value="">{selectedClientId ? (sites.length ? 'Select site…' : 'No completed jobs') : 'Pick a client first'}</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="csr-field">
              <span className="csr-label">Completed Job</span>
              <select className="csr-select" value={selectedScheduleId} onChange={(e) => setSelectedScheduleId(e.target.value)} disabled={!selectedSiteId}>
                <option value="">{selectedSiteId ? 'Select job…' : 'Pick a site first'}</option>
                {siteJobs.map((j) => <option key={j.schedule_id} value={j.schedule_id}>{fmtDate(j.scheduled_date)} · #{j.schedule_id}</option>)}
              </select>
            </div>
          </div>
        </div>

        {!selectedScheduleId ? (
          <div className="csr-card"><div className="md-empty"><div className="md-empty-icon"><FileText size={24} /></div>Pick a completed job to view its SLD report.</div></div>
        ) : loading ? (
          <div className="csr-card"><div className="md-empty"><span className="md-spinner" /><div style={{ marginTop: 12 }}>Loading report…</div></div></div>
        ) : (
          <>
            {/* Toolbar: layers + PDF */}
            <div className="csr-card" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className={`csr-layer-toggle${layerAnnots ? ' on' : ''}`} onClick={() => setLayerAnnots((v) => !v)}><Flag size={14} /> Flags</span>
              <span className={`csr-layer-toggle${layerNotes ? ' on' : ''}`} onClick={() => setLayerNotes((v) => !v)}>Notes</span>
              <span className={`csr-layer-toggle${layerPhotos ? ' on' : ''}`} onClick={() => setLayerPhotos((v) => !v)}>Photos</span>
              <button className="mj-pdf-btn" style={{ marginLeft: 'auto' }} onClick={onExportPDF} disabled={pdfBusy || !detail}>
                {pdfBusy ? <><span className="md-spinner" /> Generating…</> : <><Download size={14} /> Export PDF</>}
              </button>
            </div>

            {/* Viewer */}
            <div className="csr-card">
              <AdminSLDViewer
                diagram={diagram}
                photos={photos}
                annotations={flaggedAnnots}
                notes={notedAnnots}
                strings={strings}
                flaggedStrings={flaggedStringIds}
                baseUrl={IMG_BASE}
                layerNotes={layerNotes}
                layerPhotos={layerPhotos}
                layerAnnots={layerAnnots}
                photosLoaded={photosLoaded}
                isDark={isDark}
              />
            </div>

            {/* Annotations */}
            <div className="csr-card">
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-strong)', marginBottom: 16 }}>Field Annotations ({annotations.length})</div>
              {annotations.length === 0 ? (
                <div className="md-empty" style={{ padding: '18px 0', border: '1px dashed var(--border-main)', borderRadius: 12 }}>No annotations captured.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                  {annotations.map((a) => (
                    <div key={a.id} className="mj-annot-item">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <MapPin size={14} color="var(--accent-blue)" />
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', fontFamily: 'DM Mono,monospace', flex: 1 }}>{a.point_label || `Point #${a.point_id}`}</span>
                        {(normBool(a.flag) || a.flag === true) && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--accent-red)', background: 'var(--accent-red-light)', padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4 }}><Flag size={12} /> FLAGGED</span>}
                      </div>
                      {a.note && <div style={{ fontSize: 13, color: 'var(--text-main)', lineHeight: 1.55, fontWeight: 500 }}>{a.note}</div>}
                      {a.photo_url && <img src={safeUrl(IMG_BASE, a.photo_url)} alt="Annotation" className="mj-annot-photo" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* FSR summary */}
            {fsr && (
              <div className="csr-card">
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-strong)', marginBottom: 16 }}>Field Service Report</div>
                <div className="mj-fsr-grid">
                  <div className="mj-fsr-field"><div className="mj-fsr-field-label">Panels Cleaned</div><div className="mj-fsr-field-val">{fsr.panels_cleaned || '—'}</div></div>
                  <div className="mj-fsr-field"><div className="mj-fsr-field-label">Cable Condition</div><div className="mj-fsr-field-val">{fsr.cable_condition || '—'}</div></div>
                  {boolFields.map(([label, key]) => (
                    <div className="mj-fsr-field" key={key}>
                      <div className="mj-fsr-field-label">{label}</div>
                      <div className="mj-fsr-field-val" style={{ color: normBool(fsr[key]) ? 'var(--accent-red)' : 'var(--accent-green)' }}>{normBool(fsr[key]) ? 'Yes' : 'No'}</div>
                    </div>
                  ))}
                </div>
                {fsr.observations && <div className="mj-fsr-field" style={{ marginTop: 12 }}><div className="mj-fsr-field-label">Observations</div><div className="mj-fsr-field-val" style={{ fontWeight: 500, lineHeight: 1.6 }}>{fsr.observations}</div></div>}
                {fsr.signature_url && (
                  <div style={{ marginTop: 16 }}>
                    <div className="mj-fsr-field-label" style={{ marginBottom: 8 }}>Client Signature</div>
                    <img src={safeUrl(IMG_BASE, fsr.signature_url)} alt="Signature" style={{ maxHeight: 110, background: '#fff', borderRadius: 8, padding: 6, border: '1px solid var(--border-main)' }} />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CISLDReport;